import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { addDays, addMinutes, format, parseISO, startOfDay } from 'date-fns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiUrl } from '../config';
import {
  AppointmentAvailabilityDayDto,
  AppointmentAvailabilitySlotDto,
  AppointmentStatus,
  CustomerBookingAccessDto,
  CustomerBookingEligibilityDto,
  CustomerBookingOption,
  CustomerBookingOptionDto,
  CustomerVehicleValidationDto,
  CustomerCreateAppointmentBookingRequest,
  NextAppointmentDto,
  RedeemCustomerBookingAccessRequest,
} from '../interfaces/appointments';
import { formatDate } from '../utils/dateUtils';
import { getBookingAccessToken, saveBookingAccessToken } from './bookingAccess';

const BOOKING_WINDOW_DAYS = 120;
const BOOKING_WINDOW_START_OFFSET_DAYS = 2;
const BOOKING_CALL_PHONE = process.env.REACT_APP_BOOKING_PHONE?.trim() ?? '';
const APPOINTMENT_LOCATION = 'Skovgård AS, Jakobslivegen 2, 7058 Jakobsli';
const APPOINTMENT_SUMMARY = 'Time hos Skovgård AS';
const toDateKey = (value: Date): string => format(value, 'yyyy-MM-dd');

const toIcsUtc = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
};

const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const parseAppointmentDateTime = (date: string, time: string): Date => {
  const [year, month, day] = date.split('-').map((part) => Number(part));
  const [hour, minute] = time.split(':').map((part) => Number(part));
  return new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, 0, 0);
};

const buildCalendarDataUrl = (booking: NextAppointmentDto, registrationNumber: string): string => {
  const start = parseAppointmentDateTime(booking.date, booking.time);
  const end = addMinutes(start, booking.durationMinutes);
  const uid = `${booking.id ?? `${booking.date}-${booking.time}`}-appointment@skovgard.no`;
  const description = `Timebestilling for ${registrationNumber || 'kjoretoy'} hos Skovgård AS.`;
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Skovgard//Appointment//NO',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(APPOINTMENT_SUMMARY)}`,
    `LOCATION:${escapeIcsText(APPOINTMENT_LOCATION)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
};

const statusLabel = (status: AppointmentStatus): string => {
  switch (status) {
    case AppointmentStatus.Pending:
      return 'Mottatt';
    case AppointmentStatus.Confirmed:
      return 'Bekreftet';
    case AppointmentStatus.Rejected:
      return 'Avvist';
    case AppointmentStatus.CancelledByAdmin:
      return 'Avlyst';
    default:
      return 'Ukjent';
  }
};

const bookingOptionLabel = (option: CustomerBookingOption): string => {
  if (option === CustomerBookingOption.TireHotelChange) {
    return 'Omlegging med dekkhotell';
  }

  return 'Omlegging med egne hjul';
};

const bookingOptionDescription = (optionConfig: CustomerBookingOptionDto): string => {
  if (optionConfig.bookingOption === CustomerBookingOption.TireHotelChange) {
    return 'Tilbys for kunder med dekkhotell.';
  }

  return 'For kunder uten dekkhotell.';
};

type BookingCalendarProps = {
  selectedDate: Date | null;
  bookingWindowStart: Date;
  bookingWindowEnd: Date;
  onDateChange: (value: Date | null) => void;
  shouldDisableDate: (value: Date) => boolean;
  renderDay: (day: Date, selectedDates: Date[], pickersDayProps: PickersDayProps<Date>) => JSX.Element;
};

const BookingCalendar = React.memo(function BookingCalendar({
  selectedDate,
  bookingWindowStart,
  bookingWindowEnd,
  onDateChange,
  shouldDisableDate,
  renderDay,
}: BookingCalendarProps) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <StaticDatePicker<Date, Date>
        displayStaticWrapperAs="desktop"
        value={selectedDate}
        onChange={onDateChange}
        minDate={bookingWindowStart}
        maxDate={bookingWindowEnd}
        shouldDisableDate={shouldDisableDate}
        renderDay={renderDay}
        renderInput={(params) => <TextField {...params} sx={{ display: 'none' }} />}
      />
    </Box>
  );
});

function AppointmentBooking() {
  const { telefonnummer, registreringsnummer } = useParams<{ telefonnummer: string; registreringsnummer: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [eligibility, setEligibility] = useState<CustomerBookingEligibilityDto | null>(null);
  const [selectedBookingOption, setSelectedBookingOption] = useState<CustomerBookingOption | null>(null);
  const [availableDays, setAvailableDays] = useState<AppointmentAvailabilityDayDto[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [slots, setSlots] = useState<AppointmentAvailabilitySlotDto[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [nextBooking, setNextBooking] = useState<NextAppointmentDto | null>(null);
  const [validatedVehicle, setValidatedVehicle] = useState<CustomerVehicleValidationDto | null>(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const [loadingVehicleValidation, setLoadingVehicleValidation] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [vehicleValidationError, setVehicleValidationError] = useState<string | null>(null);
  const [bookingAccessToken, setBookingAccessToken] = useState<string | null>(null);
  const [redeemingDirectAccess, setRedeemingDirectAccess] = useState(false);

  const bookingWindowStart = useMemo(
    () => addDays(startOfDay(new Date()), BOOKING_WINDOW_START_OFFSET_DAYS),
    [],
  );
  const bookingWindowEnd = useMemo(
    () => addDays(startOfDay(new Date()), BOOKING_WINDOW_DAYS),
    [],
  );
  const hasActiveFutureBooking =
    nextBooking != null &&
    (nextBooking.status === AppointmentStatus.Pending || nextBooking.status === AppointmentStatus.Confirmed);
  const hasTireHotel = eligibility?.hasTireHotel === true;

  const bookingOptions = useMemo(() => eligibility?.options ?? [], [eligibility]);
  const availableBookingOptions = useMemo(
    () => bookingOptions.filter((option) => option.isAvailable),
    [bookingOptions],
  );
  const availableDayCountByDate = useMemo(
    () => new Map(availableDays.map((day) => [day.date, day.availableSlotCount])),
    [availableDays],
  );

  const selectedOptionConfig = useMemo(
    () => availableBookingOptions.find((option) => option.bookingOption === selectedBookingOption) ?? null,
    [availableBookingOptions, selectedBookingOption],
  );
  const validatedVehicleDisplayName = useMemo(() => {
    if (!validatedVehicle) {
      return '';
    }

    return [validatedVehicle.brand, validatedVehicle.model].filter((x) => x && x.trim().length > 0).join(' ').trim();
  }, [validatedVehicle]);
  const bookingTitle = useMemo(() => {
    if (validatedVehicleDisplayName) {
      return `Timebestilling for ${validatedVehicleDisplayName}`;
    }

    return 'Timebestilling';
  }, [validatedVehicleDisplayName]);

  const handleDateChange = useCallback((value: Date | null) => {
    if (!value) {
      return;
    }

    setSelectedDate(startOfDay(value));
    setSelectedTime('');
  }, []);

  const shouldDisableDate = useCallback(
    (value: Date) => !availableDayCountByDate.has(toDateKey(value)),
    [availableDayCountByDate],
  );

  const renderAvailabilityDay = useCallback(
    (day: Date, _selectedDates: Date[], pickersDayProps: PickersDayProps<Date>) => {
      const keyedProps = pickersDayProps as PickersDayProps<Date> & { key?: React.Key };
      const { key: dayKey, ...dayProps } = keyedProps;
      const isAvailable = availableDayCountByDate.has(toDateKey(day));
      return (
        <PickersDay
          key={dayKey ?? `${toDateKey(day)}-${pickersDayProps.outsideCurrentMonth ? 'outside' : 'current'}`}
          {...dayProps}
          sx={{
            ...dayProps.sx,
            ...(isAvailable
              ? {
                  '&:not(.Mui-selected)': {
                    color: 'success.dark',
                    fontWeight: 700,
                  },
                }
              : {}),
          }}
        />
      );
    },
    [availableDayCountByDate],
  );

  useEffect(() => {
    let cancelled = false;

    if (!telefonnummer || !registreringsnummer) {
      setBookingAccessToken(null);
      navigate('/timebestilling-login');
      return;
    }

    const token = getBookingAccessToken(telefonnummer, registreringsnummer);
    if (!token) {
      const directAccessToken = new URLSearchParams(location.search).get('access');
      if (!directAccessToken) {
        setBookingAccessToken(null);
        navigate('/timebestilling-login', {
          replace: true,
          state: { message: 'Du må logge inn og fullføre reCAPTCHA før du kan se bookingkalenderen.' },
        });
        return;
      }

      setRedeemingDirectAccess(true);
      axios
        .post<CustomerBookingAccessDto, any, RedeemCustomerBookingAccessRequest>(
          `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/direct-access/redeem`,
          { accessToken: directAccessToken },
        )
        .then((response) => {
          if (cancelled) {
            return;
          }

          saveBookingAccessToken(
            telefonnummer,
            registreringsnummer,
            response.data.token,
            response.data.expiresAtUtc,
          );
          setBookingAccessToken(response.data.token);
          navigate(location.pathname, { replace: true });
        })
        .catch(() => {
          if (cancelled) {
            return;
          }

          setBookingAccessToken(null);
          navigate('/timebestilling-login', {
            replace: true,
            state: { message: 'Lenken for direkte timebestilling er ugyldig eller utløpt. Fullfør reCAPTCHA for å fortsette.' },
          });
        })
        .finally(() => {
          if (!cancelled) {
            setRedeemingDirectAccess(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    setBookingAccessToken(token);

    return () => {
      cancelled = true;
    };
  }, [telefonnummer, registreringsnummer, location.pathname, location.search, navigate]);

  const fetchEligibility = async () => {
    if (!telefonnummer || !registreringsnummer || !bookingAccessToken) {
      return;
    }

    setLoadingEligibility(true);
    try {
      const response = await axios.get<CustomerBookingEligibilityDto>(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/eligibility`,
      );
      setEligibility(response.data);
      const defaultOption =
        response.data.options.find(
          (option) =>
            option.bookingOption === response.data.defaultBookingOption &&
            option.isAvailable,
        ) ?? response.data.options.find((option) => option.isAvailable);
      setSelectedBookingOption(defaultOption?.bookingOption ?? null);
    } catch {
      setError('Kunne ikke hente bookingvalg akkurat nå.');
      setEligibility(null);
      setSelectedBookingOption(null);
    } finally {
      setLoadingEligibility(false);
    }
  };

  const fetchVehicleValidation = async () => {
    if (!telefonnummer || !registreringsnummer) {
      return;
    }

    setLoadingVehicleValidation(true);
    setVehicleValidationError(null);

    try {
      const response = await axios.get<CustomerVehicleValidationDto>(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/vehicle/validation`,
      );
      setValidatedVehicle(response.data);
    } catch (err: any) {
      const validationMessage =
        typeof err?.response?.data === 'string' && err.response.data.trim().length > 0
          ? err.response.data
          : 'Kunne ikke validere registreringsnummer mot SVV akkurat nå. Prøv igjen senere eller ring oss på 73 90 47 10 for å bestille time.';
      setValidatedVehicle(null);
      setVehicleValidationError(validationMessage);
    } finally {
      setLoadingVehicleValidation(false);
    }
  };

  const fetchNextBooking = async () => {
    if (!telefonnummer || !registreringsnummer || !bookingAccessToken) {
      return;
    }

    try {
      const response = await axios.get<NextAppointmentDto>(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/bookings/next`,
      );
      setNextBooking(response.data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNextBooking(null);
        return;
      }

      setError('Kunne ikke hente neste time akkurat nå.');
    }
  };

  const fetchAvailableDays = async () => {
    if (!telefonnummer || !registreringsnummer || !bookingAccessToken || !selectedBookingOption || !validatedVehicle || vehicleValidationError) {
      return;
    }

    setLoadingDays(true);
    setError(null);

    const from = format(bookingWindowStart, 'yyyy-MM-dd');
    const to = format(bookingWindowEnd, 'yyyy-MM-dd');

    try {
      const response = await axios.get<AppointmentAvailabilityDayDto[]>(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/availability/days`,
        {
          params: {
            from,
            to,
            bookingOption: selectedBookingOption,
          },
        },
      );

      const days = response.data;
      setAvailableDays(days);

      if (days.length === 0) {
        setSelectedDate(null);
        setSlots([]);
        setSelectedTime('');
        return;
      }

      if (!selectedDate || !days.some((x) => x.date === format(selectedDate, 'yyyy-MM-dd'))) {
        setSelectedDate(parseISO(days[0].date));
        setSelectedTime('');
      }
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke hente ledige dager. Prøv igjen om litt.');
      }
      setAvailableDays([]);
      setSelectedDate(null);
      setSlots([]);
      setSelectedTime('');
    } finally {
      setLoadingDays(false);
    }
  };

  const fetchSlots = async (date: Date) => {
    if (!telefonnummer || !registreringsnummer || !bookingAccessToken || !selectedBookingOption || !validatedVehicle || vehicleValidationError) {
      return;
    }

    setLoadingSlots(true);

    try {
      const response = await axios.get<AppointmentAvailabilitySlotDto[]>(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/availability/slots`,
        {
          params: {
            date: format(date, 'yyyy-MM-dd'),
            bookingOption: selectedBookingOption,
          },
        },
      );

      setSlots(response.data);

      if (!selectedTime && response.data.length > 0) {
        setSelectedTime(response.data[0].time);
      } else if (!response.data.some((slot) => slot.time === selectedTime)) {
        setSelectedTime('');
      }
    } catch (err: any) {
      setSlots([]);
      setSelectedTime('');
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke hente ledige tidspunkt for valgt dato.');
      }
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (!bookingAccessToken || redeemingDirectAccess) {
      return;
    }

    fetchVehicleValidation();
    fetchEligibility();
    fetchNextBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telefonnummer, registreringsnummer, bookingAccessToken, redeemingDirectAccess]);

  useEffect(() => {
    if (!selectedBookingOption) {
      return;
    }

    if (availableBookingOptions.some((option) => option.bookingOption === selectedBookingOption)) {
      return;
    }

    setSelectedBookingOption(availableBookingOptions[0]?.bookingOption ?? null);
  }, [availableBookingOptions, selectedBookingOption]);

  useEffect(() => {
    if (!selectedBookingOption || !validatedVehicle || vehicleValidationError) {
      return;
    }

    setSelectedDate(null);
    setSelectedTime('');
    setSlots([]);
    fetchAvailableDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBookingOption, validatedVehicle, vehicleValidationError]);

  useEffect(() => {
    if (selectedDate && validatedVehicle && !vehicleValidationError) {
      fetchSlots(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedBookingOption, validatedVehicle, vehicleValidationError]);

  useEffect(() => {
    if (!vehicleValidationError) {
      return;
    }

    setAvailableDays([]);
    setSelectedDate(null);
    setSlots([]);
    setSelectedTime('');
  }, [vehicleValidationError]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!telefonnummer || !registreringsnummer) {
      setError('Mangler telefonnummer eller registreringsnummer.');
      return;
    }

    if (!selectedBookingOption) {
      setError('Velg bookingtype før bestilling.');
      return;
    }

    if (loadingVehicleValidation) {
      setError('Validerer registreringsnummer. Vent et øyeblikk og prøv igjen.');
      return;
    }

    if (!validatedVehicle || vehicleValidationError) {
      setError(vehicleValidationError ?? 'Registreringsnummeret må valideres mot SVV før bestilling.');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Velg både dato og tidspunkt før bestilling.');
      return;
    }

    if (hasActiveFutureBooking) {
      setError('Du har allerede en aktiv kommende booking. Kontakt oss hvis du trenger endring.');
      return;
    }

    if (!bookingAccessToken) {
      setError('Innloggingen er utløpt. Logg inn på nytt.');
      navigate('/timebestilling-login', {
        replace: true,
        state: { message: 'Du må logge inn og fullføre reCAPTCHA før du kan se bookingkalenderen.' },
      });
      return;
    }

    const payload: CustomerCreateAppointmentBookingRequest = {
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: selectedTime,
      bookingOption: selectedBookingOption,
      note: note.trim() || undefined,
    };

    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/appointments/customer/${telefonnummer}/${registreringsnummer}/bookings`,
        payload,
      );
      setSuccess('Timen er bestilt. Du får bekreftelse på SMS.');
      setSelectedTime('');
      setNote('');
      await Promise.all([fetchVehicleValidation(), fetchEligibility(), fetchNextBooking(), fetchAvailableDays()]);
      if (selectedDate) {
        await fetchSlots(selectedDate);
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        const conflictMessage =
          typeof err?.response?.data === 'string' && err.response.data.trim().length > 0
            ? err.response.data
            : 'Valgt tidspunkt ble akkurat opptatt. Velg et annet tidspunkt.';
        setError(conflictMessage);
        if (selectedDate) {
          await fetchSlots(selectedDate);
        }
      } else if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke sende bestillingen. Prøv igjen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ p: 2 }}>
      <Grid item xs={12} md={8} lg={6}>
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {bookingTitle}
            </Typography>
            <Typography variant="body2" color="text.primary">
              Velg tjeneste, dag og tidspunkt. Bestillinger gjort her blir automatisk bekreftet, og du mottar bekreftelse på SMS. Ved spørsmål, kontakt oss på telefon {BOOKING_CALL_PHONE || '73 90 47 10'}.
            </Typography>
          </Paper>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Neste time
              </Typography>
              {nextBooking ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip label={statusLabel(nextBooking.status)} color={nextBooking.status === AppointmentStatus.Confirmed ? 'success' : 'default'} />
                    <Chip label={`${nextBooking.durationMinutes} min`} />
                  </Stack>
                  <Typography variant="h6">
                    {formatDate(nextBooking.date, 'dd/MM/yyyy')} kl. {nextBooking.time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sted: {APPOINTMENT_LOCATION}
                  </Typography>
                  {nextBooking.adminReason && (
                    <Typography variant="body2">
                      Årsak: {nextBooking.adminReason}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      component="a"
                      href={buildCalendarDataUrl(nextBooking, registreringsnummer ?? '')}
                      download={`skovgard-time-${nextBooking.date}.ics`}
                      variant="outlined"
                      size="small"
                    >
                      Legg til i kalender
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2">Ingen kommende time funnet.</Typography>
              )}
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Alert severity="info">
                  Timebestilling må gjøres senest 2 dager i forveien. Avbestilling må skje senest 36 timer før timen. Ved
                  kortere tid enn 36 timer må du ringe Skovgård
                  {BOOKING_CALL_PHONE ? ` på ${BOOKING_CALL_PHONE}` : ''}.
                  {' '}Når du bestiller her blir timen automatisk bekreftet, og vi sender SMS-bekreftelse.
                  {BOOKING_CALL_PHONE && (
                    <>
                      {' '}
                      <a href={`tel:${BOOKING_CALL_PHONE}`}>Ring nå</a>
                    </>
                  )}
                </Alert>

                {loadingVehicleValidation && <Typography variant="body2">Validerer registreringsnummer mot SVV...</Typography>}
                {!loadingVehicleValidation && vehicleValidationError && (
                  <Alert severity="error">
                    {vehicleValidationError} Timebestilling er ikke tilgjengelig før registreringsnummeret er validert.
                  </Alert>
                )}

                {!loadingEligibility && eligibility && !hasTireHotel && (
                  <Alert severity="info">
                    Timebestilling er kun tilgjengelig for kunder med dekkhotell. Ring oss for booking på{' '}
                    <a href={`tel:${BOOKING_CALL_PHONE || '73904710'}`}>{BOOKING_CALL_PHONE || '73 90 47 10'}</a>.
                  </Alert>
                )}

                {!hasActiveFutureBooking && hasTireHotel && (
                  <>
                    <div>
                      <Typography variant="subtitle1" gutterBottom>
                        Velg tjeneste
                      </Typography>

                      {loadingEligibility && <Typography variant="body2">Laster bookingvalg...</Typography>}

                      {!loadingEligibility && (
                        <Grid container spacing={2}>
                          {availableBookingOptions.map((option) => {
                            const isSelected = selectedBookingOption === option.bookingOption;
                            return (
                              <Grid item xs={12} sm={6} key={option.bookingOption}>
                                <Card variant={isSelected ? 'outlined' : undefined}>
                                  <CardActionArea
                                    onClick={() => setSelectedBookingOption(option.bookingOption)}
                                    sx={{
                                      border: isSelected ? '2px solid' : '1px solid',
                                      borderColor: isSelected ? 'primary.main' : 'divider',
                                      minHeight: 130,
                                    }}
                                  >
                                    <CardContent>
                                      <Stack spacing={1}>
                                        <Typography variant="subtitle1">{bookingOptionLabel(option.bookingOption)}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {bookingOptionDescription(option)}
                                        </Typography>
                                      </Stack>
                                    </CardContent>
                                  </CardActionArea>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}

                      {!loadingEligibility && availableBookingOptions.length === 0 && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Ingen bookingalternativer er tilgjengelige for denne innloggingen akkurat nå.
                        </Alert>
                      )}
                    </div>

                    <div>
                      <Typography variant="subtitle1" gutterBottom>
                        Velg dag
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Vi viser ledige dager fra {formatDate(bookingWindowStart, 'dd/MM/yyyy')} til{' '}
                        {formatDate(bookingWindowEnd, 'dd/MM/yyyy')}.
                      </Typography>
                      {loadingDays && <Typography variant="body2">Laster ledige dager...</Typography>}
                      {!loadingDays && availableDays.length === 0 && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Ingen ledige timer de neste {BOOKING_WINDOW_DAYS} dagene. Ring oss for booking
                          {BOOKING_CALL_PHONE ? ` på ${BOOKING_CALL_PHONE}` : ''}.
                          {BOOKING_CALL_PHONE && (
                            <>
                              {' '}
                              <a href={`tel:${BOOKING_CALL_PHONE}`}>Ring nå</a>
                            </>
                          )}
                        </Alert>
                      )}

                      {availableDays.length > 0 && (
                        <BookingCalendar
                          selectedDate={selectedDate}
                          bookingWindowStart={bookingWindowStart}
                          bookingWindowEnd={bookingWindowEnd}
                          onDateChange={handleDateChange}
                          shouldDisableDate={shouldDisableDate}
                          renderDay={renderAvailabilityDay}
                        />
                      )}
                    </div>

                    <div>
                      <Typography variant="subtitle1" gutterBottom>
                        Velg tidspunkt
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {selectedOptionConfig
                          ? selectedOptionConfig.bookingOption === CustomerBookingOption.TireHotelChange
                            ? 'Valgt tjeneste: Omlegging med dekkhotell.'
                            : 'Valgt tjeneste: Omlegging med egne hjul.'
                          : 'Velg tjeneste for å se tider.'}
                      </Typography>

                      {loadingSlots && <Typography variant="body2">Laster ledige tidspunkt...</Typography>}

                      {!loadingSlots && slots.length === 0 && selectedDate && (
                        <Alert severity="info">Ingen ledige tidspunkt for valgt dag.</Alert>
                      )}

                      {!loadingSlots && slots.length > 0 && (
                        <Grid container spacing={1}>
                          {slots.map((slot) => (
                            <Grid item xs={4} sm={3} key={slot.time}>
                              <Button
                                fullWidth
                                variant={selectedTime === slot.time ? 'contained' : 'outlined'}
                                onClick={() => setSelectedTime(slot.time)}
                              >
                                {slot.time}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </div>

                    <TextField
                      label="Merknad (valgfri)"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      multiline
                      rows={3}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        submitting ||
                        !bookingAccessToken ||
                        loadingVehicleValidation ||
                        !validatedVehicle ||
                        Boolean(vehicleValidationError) ||
                        !selectedDate ||
                        !selectedTime ||
                        availableDays.length === 0 ||
                        !selectedBookingOption
                      }
                    >
                      {submitting ? 'Sender...' : 'Bestill time'}
                    </Button>
                  </>
                )}
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default AppointmentBooking;
